import { DocumentError, DocumentSource, SOC2Scope } from '@/gen/artifacts/v1/model_pb'
import { AssessmentScore } from '@/gen/assessment/v1/assessment_score_pb'
import { ControlStatus } from '@/gen/controls/v1/control_status_pb'
import { UserStatus } from '@/gen/customer/management/v1/service_pb'
import { AICapability, AIPosture } from '@/gen/inventory/v1/ai_posture_pb'
import { AlertStatus } from '@/gen/inventory/v1/alert_pb'
import { AssessmentDecision } from '@/gen/inventory/v1/assessment_decision_pb'
import { CompanyLifecycle } from '@/gen/inventory/v1/company_lifecycle_pb'
import {
  InherentRiskSource,
  PermissionCategory,
  PointOfContactChangeType,
} from '@/gen/inventory/v1/company_service_pb'
import { CompanyStatus } from '@/gen/inventory/v1/company_status_pb'
import {
  InherentRiskSetting,
  SmartEvidenceResponseType,
  SmartQuestionStatus,
} from '@/gen/inventory/v1/control_service_pb'
import { CustomFieldEdgeType, CustomFieldType } from '@/gen/inventory/v1/custom_field_service_pb'
import { Implication } from '@/gen/inventory/v1/implication_pb'
import { IntegrationCategory } from '@/gen/inventory/v1/integration_service_pb'
import { OngoingAssessmentStatus } from '@/gen/inventory/v1/ongoing_assessment_pb'
import { ProcurementRequestStatus } from '@/gen/inventory/v1/requests_service_pb'
import { RiskLevel, RiskType } from '@/gen/inventory/v1/risk_pb'
import { SignalType } from '@/gen/inventory/v1/signal_pb'
import { QuestionnaireStatus } from '@/gen/questionnaire/common/v1/questionnaire_status_pb'
import { ManualQuestionStatus } from '@/gen/questionnaire/v2/model_pb'

import { DocumentStatus } from '@/const/artifact-status.types'

import { RequiredStatus } from '@/pages/questionnaire/sub-category-required/sub-category-required-context'

const reverseLabelMap = <T extends number>(labelMap: Record<T, string>): Record<string, T> => {
  return Object.fromEntries(
    Object.entries(labelMap).map(([key, value]) => [value, parseInt(key) as T]),
  ) as Record<string, T>
}

// "Not started" is used by the questionnaire status hook when 0 questions are answered, regardless of status.
export const questionnaireStatusLabel: Record<QuestionnaireStatus, string> = {
  [QuestionnaireStatus.UNSPECIFIED]: 'In progress',
  [QuestionnaireStatus.DRAFT]: 'In progress',
  [QuestionnaireStatus.ONGOING]: 'In progress',
  [QuestionnaireStatus.EXPIRED]: 'In progress',
  [QuestionnaireStatus.SUBMITTED]: 'Submitted',
  [QuestionnaireStatus.COMPLETED]: 'Finalized',
}

export const smartQuestionStatusLabel: Record<SmartQuestionStatus, string> = {
  [SmartQuestionStatus.UNSPECIFIED]: '',
  [SmartQuestionStatus.UNPUBLISHED]: 'Not shared',
  [SmartQuestionStatus.PUBLISHED]: 'Shared',
  [SmartQuestionStatus.AWAITING_REVIEW]: 'Ready for review',
  [SmartQuestionStatus.AWAITING_RECIPIENT]: 'Awaiting recipient',
  [SmartQuestionStatus.RESOLVED]: 'Resolved',
  [SmartQuestionStatus.AWAITING_REOPEN]: 'Awaiting reopen',
}

export const smartEvidenceResponseTypeLabel: Record<SmartEvidenceResponseType, string> = {
  [SmartEvidenceResponseType.UNSPECIFIED]: '',
  [SmartEvidenceResponseType.ACCEPTED]: 'Accepted',
  [SmartEvidenceResponseType.REJECTED]: 'Rejected',
  [SmartEvidenceResponseType.COMMENTED]: 'Commented',
}

export const requiredStatusLabel: Record<Exclude<RequiredStatus, 'required'>, string> = {
  notDetermined: 'Answer the above questions first',
  notRequired: "You don't need to answer this question",
}

export const categoryLabel: Record<IntegrationCategory, string> = {
  [IntegrationCategory.UNSPECIFIED]: '',
  [IntegrationCategory.API_GATEWAY]: 'API gateway',
  [IntegrationCategory.SECURITY_STACK]: 'Security stack',
  [IntegrationCategory.FILE_SHARING]: 'File sharing',
  [IntegrationCategory.SASE]: 'SASE',
  [IntegrationCategory.PAYMENT]: 'Payment gateway',
  [IntegrationCategory.CLOUD_INFRASTRUCTURE]: 'Cloud infrastructure',
  [IntegrationCategory.GRC]: 'GRC',
  [IntegrationCategory.PROCUREMENT]: 'Procurement',
  [IntegrationCategory.IDP]: 'IDP',
  [IntegrationCategory.MAIL]: 'Mail',
  [IntegrationCategory.ISSUE_MANAGEMENT]: 'Issue management',
  [IntegrationCategory.AI_TOOLS]: 'AI tools',
}

export const assessmentDecisionLabel: Record<AssessmentDecision, string> = {
  [AssessmentDecision.UNSPECIFIED]: '',
  [AssessmentDecision.APPROVED]: 'Approved',
  [AssessmentDecision.CONDITIONALLY_APPROVED]: 'Conditionally approved',
  [AssessmentDecision.REJECTED]: 'Rejected',
}

export const assessmentDecisionButtonLabel: Record<AssessmentDecision, string> = {
  [AssessmentDecision.APPROVED]: 'Approve',
  [AssessmentDecision.CONDITIONALLY_APPROVED]: 'Conditionally approve',
  [AssessmentDecision.REJECTED]: 'Reject',
  [AssessmentDecision.UNSPECIFIED]: '',
}

export const ongoingAssessmentStatusLabel: Record<OngoingAssessmentStatus, string> = {
  [OngoingAssessmentStatus.UNSPECIFIED]: '',
  [OngoingAssessmentStatus.READY_TO_START]: 'Ready to start',
  [OngoingAssessmentStatus.IN_PROGRESS]: 'In progress',
  [OngoingAssessmentStatus.AWAITING_APPROVAL]: 'Awaiting approval',
  [OngoingAssessmentStatus.COMPLETED]: 'Completed',
}

export const pointOfContactChangeTitle: Record<PointOfContactChangeType, string> = {
  [PointOfContactChangeType.UNSPECIFIED]: '',
  [PointOfContactChangeType.CREATE]: 'Contact created: ',
  [PointOfContactChangeType.ACTIVATE]: 'Contact activated: ',
  [PointOfContactChangeType.DEACTIVATE]: 'Contact deactivated: ',
}

export const procurementRequestStatusLabel: Record<ProcurementRequestStatus, string> = {
  [ProcurementRequestStatus.UNSPECIFIED]: '',
  [ProcurementRequestStatus.PENDING]: 'Pending',
  [ProcurementRequestStatus.APPROVED]: 'Approved',
  [ProcurementRequestStatus.REJECTED]: 'Rejected',
}

// Map risk level labels back to enum values
export type RiskLabel = 'Low' | 'Medium' | 'High' | 'Critical' | ''

export const RISK_LABEL_TO_LEVEL_MAP: Record<RiskLabel, RiskLevel> = {
  Low: RiskLevel.LOW,
  Medium: RiskLevel.MEDIUM,
  High: RiskLevel.HIGH,
  Critical: RiskLevel.CRITICAL,
  '': RiskLevel.UNSPECIFIED,
} as const

export const riskTypeLabel: Record<RiskType, string> = {
  [RiskType.UNSPECIFIED]: 'Research',
  [RiskType.INSECURE_DEFAULTS]: 'Insecure defaults',
  [RiskType.DRIFT_OF_SCOPE]: 'Drift of scope',
}

// Not a signal type: a risk the classifier hasn't typed yet has no title to fetch.
export const UNCLASSIFIED_SIGNAL_TYPE_LABEL = 'Unclassified'

export const signalTypeTitlesFrom = (
  options: { value: SignalType; label: string }[],
): ReadonlyMap<SignalType, string> =>
  new Map([
    [SignalType.UNSPECIFIED, UNCLASSIFIED_SIGNAL_TYPE_LABEL],
    ...options.map((o): [SignalType, string] => [o.value, o.label]),
  ])

export const riskLevelLabel: Record<RiskLevel, keyof typeof RISK_LABEL_TO_LEVEL_MAP> = {
  [RiskLevel.UNSPECIFIED]: '',
  [RiskLevel.CRITICAL]: 'Critical',
  [RiskLevel.HIGH]: 'High',
  [RiskLevel.MEDIUM]: 'Medium',
  [RiskLevel.LOW]: 'Low',
} as const

export const companyStatusLabel: Record<CompanyStatus, string> = {
  [CompanyStatus.UNSPECIFIED]: '',
  [CompanyStatus.ASSESSMENT_REQUIRED]: 'Assessment required',
  [CompanyStatus.IN_ASSESSMENT]: 'In assessment',
  [CompanyStatus.APPROVED]: 'Approved',
  [CompanyStatus.REJECTED]: 'Rejected',
}

export const stringToCompanyStatus = reverseLabelMap<CompanyStatus>(companyStatusLabel)

export const assessmentScoreLabel: Record<AssessmentScore, string> = {
  [AssessmentScore.UNSPECIFIED]: '',
  [AssessmentScore.ACCEPTABLE]: 'Acceptable',
  [AssessmentScore.CONCERNING]: 'Concerning',
  [AssessmentScore.UNACCEPTABLE]: 'Unacceptable',
  [AssessmentScore.NOT_ASSESSED]: 'Never assessed',
}
export const companyLifecycleLabel: Record<CompanyLifecycle, string> = {
  [CompanyLifecycle.UNSPECIFIED]: '',
  [CompanyLifecycle.IN_EVALUATION]: 'In evaluation',
  [CompanyLifecycle.ONBOARDED]: 'Onboarded',
  [CompanyLifecycle.ARCHIVED]: 'Archived',
  [CompanyLifecycle.UNSANCTIONED]: 'Unsanctioned',
  [CompanyLifecycle.OFFBOARDED]: 'Offboarded',
}

export const aiPostureLabel: Record<AIPosture, string> = {
  [AIPosture.AI_POSTURE_UNSPECIFIED]: 'AI not detected',
  [AIPosture.AI_POSTURE_NON_AI]: 'AI not detected',
  [AIPosture.AI_POSTURE_MODEL_PROVIDER]: 'Model provider',
  [AIPosture.AI_POSTURE_AI_NATIVE]: 'AI native',
  [AIPosture.AI_POSTURE_EMBEDDED_AI]: 'Embedded AI',
}

export const aiCapabilityLabel: Record<AICapability, string> = {
  [AICapability.AI_CAPABILITY_UNSPECIFIED]: '',
  [AICapability.AI_CAPABILITY_AUTONOMOUS_AGENT]: 'Autonomous agent',
  [AICapability.AI_CAPABILITY_SCHEDULED_EXECUTION]: 'Scheduled execution',
  [AICapability.AI_CAPABILITY_CODE_EXECUTION]: 'Code execution',
  [AICapability.AI_CAPABILITY_WEB_ACCESS]: 'Web access',
  [AICapability.AI_CAPABILITY_COMPUTER_BROWSER_CONTROL]: 'Computer/browser control',
  [AICapability.AI_CAPABILITY_DATA_ACCESS]: 'Data access',
  [AICapability.AI_CAPABILITY_EXTERNAL_SYSTEM_ACTIONS]: 'External system actions',
  [AICapability.AI_CAPABILITY_MCP]: 'MCP',
  [AICapability.AI_CAPABILITY_PERSISTENT_MEMORY]: 'Persistent memory',
  [AICapability.AI_CAPABILITY_GENERATIVE_AI]: 'Generative AI',
  [AICapability.AI_CAPABILITY_OTHER]: 'Other',
}

export const controlStatusLabel: Record<ControlStatus, string> = {
  [ControlStatus.UNSPECIFIED]: '',
  [ControlStatus.VALIDATED]: 'Validated',
  [ControlStatus.GAP]: 'Gap',
  [ControlStatus.NO_EVIDENCE]: 'No evidence',
  [ControlStatus.NOT_EVALUATED]: 'Not evaluated',
  [ControlStatus.NOT_APPLICABLE]: 'Not applicable',
  [ControlStatus.ATTESTED]: 'Attested',
}

/** get human readable label to be displayed in UI for a given permission category
 *  Note: having a default value for category is not necessary,
 * because without it passing PermissionCategory.UNSPECIFIED will return undefined instead of 'Other' */
export const getPermissionLabel = (category: PermissionCategory) => {
  return permissionLabel[category || PermissionCategory.UNSPECIFIED]
}

const permissionLabel: Record<PermissionCategory, string> = {
  [PermissionCategory.UNSPECIFIED]: 'Other',
  [PermissionCategory.DATA_ACCESS]: 'Data access',
  [PermissionCategory.DATA_MODIFICATION]: 'Data modification',
  [PermissionCategory.OPERATIONAL_ENVIRONMENT]: 'Operational environment modification',
  [PermissionCategory.IDENTITY_AND_ACCESS_MANAGEMENT]: 'Identity & access management',
  [PermissionCategory.CONFIGURATION_PERMISSIONS]: 'Configuration permissions',
  [PermissionCategory.EMPLOYEE_DATA_ACCESS]: 'Employee data access',
  [PermissionCategory.EMPLOYEE_DATA_MODIFICATION]: 'Employee data modification',
  [PermissionCategory.CLOUD_ENVIRONMENT]: 'Cloud environment',
  [PermissionCategory.HIGH_PRIVILEGE_APP]: 'High privilege app',
  [PermissionCategory.NETWORK_ACCESS]: 'Network access',
}

export const implicationLabel: Record<Implication, string> = {
  [Implication.BREACH_IMPACT]: 'Impact',
  [Implication.BREACH_LIKELIHOOD]: 'Likelihood',
  [Implication.COMPLIANCE]: 'Compliance',
  [Implication.BUSINESS_CONTINUITY]: 'Business continuity',
  [Implication.UNSPECIFIED]: '',
}

export const alertStatusLabel: Record<AlertStatus, string> = {
  [AlertStatus.RESOLVED]: 'Resolved',
  [AlertStatus.ACCEPTED]: 'Accepted',
  [AlertStatus.ONGOING]: 'Ongoing',
  [AlertStatus.OPEN]: 'Open',
  [AlertStatus.NOT_APPLICABLE]: 'Not applicable',
  [AlertStatus.UNSPECIFIED]: '',
}

export const documentStatusLabel: Record<DocumentStatus, string> = {
  [DocumentStatus.PENDING]: 'Pending',
  [DocumentStatus.CLASSIFYING]: 'Classifying',
  [DocumentStatus.COMPLETE]: 'Complete',
  [DocumentStatus.UNSPECIFIED]: '',
}

export const documentErrorLabel: Record<DocumentError, string> = {
  [DocumentError.UNSPECIFIED]: '',
  [DocumentError.EMPTY]: 'No content',
  [DocumentError.ENCRYPTED]: 'Encrypted',
  [DocumentError.UNKNOWN_CATEGORY]: 'Unknown category',
  [DocumentError.UNSUPPORTED_TYPE]: 'Format error',
  [DocumentError.UNKNOWN_COMPANY]: 'Unknown third-party',
  [DocumentError.NO_TEXT_EXTRACTED]: 'Not readable',
  [DocumentError.UNKNOWN_ERROR]: 'Failed',
}

export const userStatusLabel: Record<UserStatus, string> = {
  [UserStatus.ENABLED]: 'Enabled',
  [UserStatus.DISABLED]: 'Disabled',
  [UserStatus.INVITED]: 'Invited',
  [UserStatus.UNSPECIFIED]: '',
}

export const documentSourceLabel: Record<DocumentSource, string> = {
  [DocumentSource.UNSPECIFIED]: '-',
  [DocumentSource.MANUAL_UPLOAD]: 'Manual upload',
  [DocumentSource.INTEGRATION]: 'Integration',
  [DocumentSource.WEB]: 'Web',
  [DocumentSource.MANUAL_UPLOAD_WITH_COMPANY]: 'Manual upload',
  [DocumentSource.MANUAL_UPLOAD_WITH_COMPANY_AND_CATEGORY]: 'Manual upload',
  [DocumentSource.LEMA_QUESTIONNAIRE]: 'Lema',
}

export const inherentRiskSourceLabel: Record<InherentRiskSource, string> = {
  [InherentRiskSource.UNSPECIFIED]: '',
  [InherentRiskSource.PROJECTED]: 'Projected',
  [InherentRiskSource.IRQ]: 'IRQ',
  [InherentRiskSource.INTEGRATION]: 'Integration',
  [InherentRiskSource.MICROSOFT_ENTRA_ID]: 'Integration',
  [InherentRiskSource.GOOGLE_WORKSPACE]: 'Integration',
  [InherentRiskSource.ZIP]: 'Integration',
  [InherentRiskSource.WIZ]: 'Integration',
  [InherentRiskSource.NETSKOPE]: 'Integration',
  [InherentRiskSource.OKTA]: 'Integration',
  [InherentRiskSource.WEBHOOK]: 'Integration',
  [InherentRiskSource.VENDR]: 'Integration',
  [InherentRiskSource.ASANA]: 'Integration',
  [InherentRiskSource.RAMP]: 'Integration',
  [InherentRiskSource.ONETRUST]: 'Integration',
  [InherentRiskSource.GRAPHITECONNECT]: 'Integration',
  [InherentRiskSource.LOGICMANAGER]: 'Integration',
  [InherentRiskSource.INTAKE]: 'Intake',
}

export const questionStatusLabel: Record<ManualQuestionStatus, string> = {
  [ManualQuestionStatus.UNANSWERED]: 'Unanswered',
  [ManualQuestionStatus.NEEDS_REVIEW]: 'Needs review',
  [ManualQuestionStatus.REJECTED]: 'Rejected',
  [ManualQuestionStatus.ACCEPTED]: 'Accepted',
  [ManualQuestionStatus.UNSPECIFIED]: '',
}

export const recipientQuestionStatusLabel: Record<ManualQuestionStatus, string> = {
  ...questionStatusLabel,
  [ManualQuestionStatus.NEEDS_REVIEW]: 'Awaiting review',
}

export const soc2ScopeLabel: Record<SOC2Scope, string> = {
  [SOC2Scope.SOC2_SCOPE_UNSPECIFIED]: '',
  [SOC2Scope.SOC2_SCOPE_SECURITY]: 'Security',
  [SOC2Scope.SOC2_SCOPE_AVAILABILITY]: 'Availability',
  [SOC2Scope.SOC2_SCOPE_CONFIDENTIALITY]: 'Confidentiality',
  [SOC2Scope.SOC2_SCOPE_PRIVACY]: 'Privacy',
  [SOC2Scope.SOC2_SCOPE_PROCESSING]: 'Processing integrity',
}

export const customFieldTypeLabel: Record<CustomFieldType, string> = {
  [CustomFieldType.UNSPECIFIED]: '',
  [CustomFieldType.TEXT]: 'Text',
  [CustomFieldType.MULTI_SELECT]: 'Multi-select',
  [CustomFieldType.DATE]: 'Date',
}

export const customFieldEdgeTypeLabel: Record<CustomFieldEdgeType, string> = {
  [CustomFieldEdgeType.UNSPECIFIED]: '',
  [CustomFieldEdgeType.ORG_COMPANY]: 'Third-party',
  [CustomFieldEdgeType.ORG_COMPANY_ENGAGEMENT]: 'Engagement',
}

export const artifactSubcategoryLabel = {
  REPORT_SOC2_TYPE_2: 'SOC2 Type II',
} as const

export const assessmentDueStatusLabel = {
  upToDate: 'Scheduled',
  dueSoon: 'Due soon',
  overdue: 'Overdue',
  noDueDate: 'Never assessed',
} as const

export const controlStatusToLemaInsight: Record<ControlStatus, string> = {
  [ControlStatus.VALIDATED]: 'Yes',
  [ControlStatus.GAP]: 'No',
  [ControlStatus.NO_EVIDENCE]: 'No evidence',
  [ControlStatus.NOT_EVALUATED]: 'Not evaluated',
  [ControlStatus.NOT_APPLICABLE]: 'Not applicable',
  [ControlStatus.ATTESTED]: 'Attested',
  [ControlStatus.UNSPECIFIED]: '',
}

export const inherentRiskDisplayLabel: Record<InherentRiskSetting, string> = {
  [InherentRiskSetting.UNSPECIFIED]: '',
  [InherentRiskSetting.CRITICAL_AND_ABOVE]: 'Critical',
  [InherentRiskSetting.HIGH_AND_ABOVE]: 'High or critical',
  [InherentRiskSetting.MEDIUM_AND_ABOVE]: 'Medium and above',
  [InherentRiskSetting.LOW_AND_ABOVE]: 'Low and above (always)',
}

export const progressStatusLabel = {
  NOT_STARTED: 'Not started',
  IN_PROGRESS: 'In progress',
  COMPLETED: 'Completed',
} as const

export const SECTION_LABELS = {
  assessments: 'Assessments',
  requests: 'Requests',
  thirdParties: 'Third-parties',
  alerts: 'Alerts',
  outreach: 'Outreach',
  dashboards: 'Dashboards',
} as const

export const WHY_THIS_MATTERS_LABEL = 'Why this matters'
